// Parses schema.org FAQPage content directly out of an article's already-published HTML,
// instead of requiring a separate "faqs" field the CMS doesn't provide.
//
// Google's structured-data guidelines require FAQPage markup to match content the visitor can
// actually see on the page. The CMS authors blog FAQs as a heading that reads as a question,
// immediately followed by its answer paragraph(s) — this walks the real HTML tree (via
// htmlparser2, the same dependency-free parser sanitizeRichHtml already uses under
// sanitize-html) and pairs them up. Because it's driven by the post's own content, every blog
// post with a visible FAQ section gets correct FAQPage markup automatically.
//
// Ported from NevTan Sign's src/utils/extractFaqFromHtml.js, plus its
// faqPageSchemaFromHtml() wrapper (normally in src/constant/seo.js there, but this app has no
// equivalent shared SEO helper file, so it lives here next to the extractor it wraps).
import { Parser } from 'htmlparser2'

const HEADING_TAGS = new Set(['h2', 'h3', 'h4', 'h5', 'h6'])
const ANSWER_TAGS = new Set(['p', 'li'])

// Below these lengths the text is almost certainly a stray heading/fragment, not a real Q&A
// pair — skip it rather than emit low-quality structured data.
const MIN_QUESTION_LENGTH = 8
const MIN_ANSWER_LENGTH = 15

interface Block {
  tag: string
  text: string
}

export interface Faq {
  question: string
  answer: string
}

/**
 * Flattens the HTML into an ordered list of { tag, text } blocks — one per
 * heading/paragraph/list-item, with nested inline markup (links, bold, etc.) collapsed down to
 * its plain text. Using a real parser (rather than a regex over raw HTML) means attribute
 * variations, self-closing tags, nesting, and HTML entities are all handled correctly.
 */
function flattenToBlocks(html: string): Block[] {
  const blocks: Block[] = []
  let currentTag: string | null = null
  let buffer = ''

  const flush = () => {
    const text = buffer.replace(/\s+/g, ' ').trim()
    if (currentTag && text) blocks.push({ tag: currentTag, text })
    buffer = ''
  }

  const parser = new Parser(
    {
      onopentag(name) {
        const tag = name.toLowerCase()
        if (HEADING_TAGS.has(tag) || ANSWER_TAGS.has(tag)) {
          flush()
          currentTag = tag
        }
      },
      ontext(text) {
        if (currentTag) buffer += text
      },
      onclosetag(name) {
        if (name.toLowerCase() === currentTag) flush()
      },
    },
    { decodeEntities: true },
  )

  parser.write(html || '')
  parser.end()
  flush()

  return blocks
}

/**
 * `html` should be the same sanitized body HTML the page actually renders (post-sanitizeRichHtml)
 * so the extracted Q&A can never drift from what a visitor or crawler sees. Returns [] when no
 * heading in the article reads as a question — callers must treat that as "no FAQ schema for
 * this page", never emit an empty mainEntity array.
 */
export function extractFaqFromHtml(html?: string | null): Faq[] {
  const blocks = flattenToBlocks(html || '')
  const faqs: Faq[] = []
  let pendingQuestion: string | null = null
  let pendingAnswerParts: string[] = []

  const commit = () => {
    if (pendingQuestion) {
      const answer = pendingAnswerParts.join(' ').trim()
      if (answer.length >= MIN_ANSWER_LENGTH) {
        faqs.push({ question: pendingQuestion, answer })
      }
    }
    pendingQuestion = null
    pendingAnswerParts = []
  }

  for (const block of blocks) {
    if (HEADING_TAGS.has(block.tag)) {
      commit()
      const looksLikeQuestion = block.text.endsWith('?') && block.text.length >= MIN_QUESTION_LENGTH
      pendingQuestion = looksLikeQuestion ? block.text : null
    } else if (pendingQuestion) {
      pendingAnswerParts.push(block.text)
    }
  }
  commit()

  return faqs
}

/**
 * FAQPage structured data for a blog post, or null when the post has no heading that reads as
 * a question — Google treats an empty `mainEntity` as invalid, so the caller must skip
 * rendering the script tag entirely in that case rather than emit one.
 */
export function faqPageSchemaFromHtml(
  bodyHtml: string,
  pageUrl: string,
): Record<string, unknown> | null {
  const faqs = extractFaqFromHtml(bodyHtml)
  if (faqs.length === 0) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': `${pageUrl}#faqpage`,
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  }
}
